import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, BellRing, ShieldCheck } from "lucide-react";

/**
 * A phone that completes its own application, sends it to the panel and lands on terms. It cycles
 * through three very different cases so the range is obvious — a £450k receivables facility reads
 * nothing like an £18m portfolio refinance, but the journey is identical.
 */
const CASES = [
  {
    fields: [
      { label: "Business", value: "Harlow Industrial Group" },
      { label: "Facility required", value: "£4,200,000" },
      { label: "Purpose", value: "Plant refinance + capex" },
      { label: "Trading since", value: "1998" },
    ],
    offer: "Terms received · £4.2m",
    detail: "Asset refinance over 5 years.",
    matched: 3,
  },
  {
    fields: [
      { label: "Business", value: "Kestrel Care Holdings" },
      { label: "Facility required", value: "£18,000,000" },
      { label: "Purpose", value: "Portfolio refinance, 4 sites" },
      { label: "Trading since", value: "2006" },
    ],
    offer: "Terms received · £18m",
    detail: "7-year term, one lead funder.",
    matched: 2,
  },
  {
    fields: [
      { label: "Business", value: "Novafab Ltd" },
      { label: "Facility required", value: "£450,000" },
      { label: "Purpose", value: "Receivables facility" },
      { label: "Trading since", value: "2014" },
    ],
    offer: "Terms received · £450k",
    detail: "85% advance, live in five days.",
    matched: 4,
  },
];

const LANES = 5;

export default function PhoneApproval() {
  const reduce = useReducedMotion();
  const [caseIx, setCaseIx] = useState(0);
  const [phase, setPhase] = useState(reduce ? 3 : 0);   // 0 typing · 1 routing · 2 reviewing · 3 terms
  const [typed, setTyped] = useState(reduce ? 4 : 0);
  const [alert, setAlert] = useState(false);            // the push notification: in, read, gone

  const active = CASES[caseIx];

  useEffect(() => {
    if (reduce) return;
    const timers = [];
    const step = 620;
    const fields = CASES[0].fields.length;
    setPhase(0); setTyped(0); setAlert(false);
    CASES[0].fields.forEach((_, i) => timers.push(setTimeout(() => setTyped(i + 1), 700 + i * step)));
    timers.push(setTimeout(() => setPhase(1), 700 + fields * step));
    timers.push(setTimeout(() => setPhase(2), 1500 + fields * step));
    timers.push(setTimeout(() => { setPhase(3); setAlert(true); }, 3400 + fields * step));
    timers.push(setTimeout(() => setAlert(false), 6400 + fields * step));
    timers.push(setTimeout(() => setCaseIx((c) => (c + 1) % CASES.length), 9800 + fields * step));
    return () => timers.forEach(clearTimeout);
  }, [reduce, caseIx]);

  return (
    <div className="relative mx-auto w-full max-w-[330px]" data-testid="phone-approval">
      <div aria-hidden className={`absolute -inset-10 rounded-full blur-[90px] transition-colors duration-1000 ${
        phase === 3 ? "bg-[#22c55e]/25" : "bg-[#0055FF]/20"}`} />

      <div className="relative rounded-[2.6rem] border border-white/15 bg-[#070C16] p-2.5 shadow-[0_50px_120px_-40px_rgba(0,0,0,1)]">
        <div className="relative overflow-hidden rounded-[2.1rem] border border-white/10 bg-gradient-to-b from-[#0E1728] to-[#0A101E] aspect-[9/16.5]">
          <div className="flex items-center justify-between px-5 pt-4 text-[10px] text-slate-400 font-mono">
            <span>09:41</span>
            <span className="flex items-center gap-1"><span className="size-1.5 rounded-full bg-[#22c55e]" /> secure</span>
          </div>

          <div className="px-5 pt-4">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center size-6 rounded-md bg-[#0055FF] text-white text-[10px] font-bold">C</span>
              <span className="text-[11px] font-semibold text-white">Connexis application</span>
            </div>
            <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
              <motion.div className="h-full bg-[#0055FF]"
                          animate={{ width: phase === 0 ? `${(typed / active.fields.length) * 70 + 8}%` : "100%" }}
                          transition={{ duration: 0.5, ease: "easeOut" }} />
            </div>
          </div>

          <div className="px-5 pt-5 space-y-2.5">
            {active.fields.map((f, i) => {
              const done = typed > i;
              return (
                <div key={f.label}
                     className={`rounded-xl border px-3 py-2.5 transition-colors duration-500 ${
                       done ? "border-white/12 bg-white/[0.05]" : "border-white/[0.07] bg-white/[0.02]"}`}>
                  <div className="text-[8.5px] uppercase tracking-[0.16em] text-slate-500">{f.label}</div>
                  <div className="mt-0.5 flex items-center gap-1.5 min-h-[15px]">
                    {done ? (
                      <>
                        <motion.span initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }}
                                     className="text-[11.5px] font-medium text-white leading-tight">{f.value}</motion.span>
                        <Check className="size-3 text-[#22c55e] shrink-0" />
                      </>
                    ) : (
                      <span className="inline-block h-3 w-px bg-[#7da9ff] animate-pulse" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-5 pt-4">
            <div className={`relative h-11 rounded-xl grid place-items-center overflow-hidden transition-colors duration-500 ${
              phase === 0 ? "bg-[#0055FF]/25 border border-[#0055FF]/30"
                          : phase >= 2 ? "bg-[#22c55e]/15 border border-[#22c55e]/30"
                                       : "bg-[#0055FF] border border-[#0055FF]"}`}>
              {!reduce && phase === 1 && (
                <motion.span aria-hidden className="absolute inset-0 bg-white/20"
                             initial={{ x: "-100%" }} animate={{ x: "100%" }}
                             transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }} />
              )}
              <span className={`relative text-[11.5px] font-semibold tracking-tight ${
                phase >= 2 ? "text-[#22c55e]" : phase === 0 ? "text-[#7da9ff]" : "text-white"}`}>
                {phase === 0 ? "Send to funders" : phase === 1 ? "Sending securely…" : "Sent · with the panel"}
              </span>
            </div>
          </div>

          <div className="px-5 pt-3">
            <AnimatePresence mode="wait">
              {phase >= 1 && (
                <motion.div key={phase === 1 ? "sending" : "panel"}
                            initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}
                            className="rounded-xl border border-white/10 bg-[#0055FF]/[0.07] px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-[0.16em] text-[#7da9ff]">
                    {phase === 1 ? "Matching on criteria"
                                 : phase === 2 ? `${active.matched} funders reviewing` : "Terms received"}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    {Array.from({ length: LANES }).map((_, i) => (
                      <motion.span key={i} aria-hidden className="h-1.5 flex-1 rounded-full"
                                   animate={{
                                     backgroundColor: phase === 1
                                       ? ["rgba(255,255,255,0.12)", "rgba(125,169,255,0.85)", "rgba(255,255,255,0.12)"]
                                       : phase >= 2 && i < active.matched ? "rgba(34,197,94,0.85)" : "rgba(255,255,255,0.12)",
                                   }}
                                   transition={phase === 1
                                     ? { duration: 1.1, repeat: Infinity, delay: i * 0.12 }
                                     : { duration: 0.5, delay: i * 0.08 }} />
                    ))}
                  </div>
                  {phase >= 2 && (
                    <div className="mt-2.5 text-[10px] leading-snug text-slate-400">
                      {phase === 2 ? "Nothing further needed from you." : active.detail}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* the moment — arrives like a push notification, then clears */}
          <AnimatePresence>
            {alert && (
              <motion.div
                initial={reduce ? false : { opacity: 0, y: -22, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="absolute left-4 right-4 top-10 rounded-xl border border-[#22c55e]/35 bg-[#0A1A12]/95 backdrop-blur-md px-3 py-2.5 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.9)]"
                data-testid="phone-approval-offer">
                <div className="flex items-center gap-2.5">
                  <BellRing className="size-3.5 text-[#22c55e] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11.5px] font-semibold text-white leading-tight truncate">{active.offer}</div>
                    <div className="text-[9.5px] text-slate-400 leading-snug truncate">{active.detail}</div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-6 bg-gradient-to-t from-[#0A101E] via-[#0A101E]/90 to-transparent">
            <div className="flex items-center gap-1.5 text-[9.5px] text-slate-500">
              <ShieldCheck className="size-3 text-[#7da9ff]" /> Documents stay in the Connexis vault
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-[11px] text-slate-500">
        Illustration of the journey — three different cases, one process.
      </p>
    </div>
  );
}
